import { off, onValue, ref as firebaseRef, type DataSnapshot, type Database, type DatabaseReference, type Query } from 'firebase/database'
import { ref, type Ref } from 'vue'
import { Result } from '@cplug/frontend-core/utils'
import { ErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { CPBaseCRUDGateway } from './CPBaseCRUDGateway'
import { useFirebase } from '~/shared/services/firebase'
import { useFirebaseAuth } from '~/shared/firebase/composables/useFirebaseAuth'

/**
 * Unsubscribe function
 */
export type UnsubscribeFunction = () => void

/**
 * Firebase subscription
 */
interface FirebaseSubscription {
  reference: DatabaseReference | Query
  unsubscribe: UnsubscribeFunction
  path: string
}

/**
 * Firebase subscription with refs cache for list
 */
interface FirebaseListSubscription extends FirebaseSubscription {
  data: Ref<any[]>
  error: Ref<BaseError | null>
  isConnected: Ref<boolean>
  invalidItemsCount: Ref<number>
  refCount: number
}

/**
 * Firebase subscription with refs cache for item
 */
interface FirebaseItemSubscription extends FirebaseSubscription {
  data: Ref<any | null>
  error: Ref<BaseError | null>
  isConnected: Ref<boolean>
  refCount: number
}

/**
 * Firebase service
 */
interface FirebaseService {
  database: Database | null
  isInitialized: boolean
}

/**
 * Base model with optional id field for Firebase
 */
export interface FirebaseModel {
  id?: string | number
}

/**
 * Base gateway
 */
export abstract class CPBaseFirebaseGateway<
  DTO,
  ListDTO extends { [key: string]: DTO[] },
  Model extends FirebaseModel
> extends CPBaseCRUDGateway<DTO, ListDTO, Model> {
  private static firebaseService: FirebaseService | null = null
  private static activeInstances = new Set<CPBaseFirebaseGateway<any, any, any>>()
  private activeSubscriptions = new Map<string, FirebaseSubscription>()
  private activeListSubscriptions = new Map<string, FirebaseListSubscription>()
  private activeItemSubscriptions = new Map<string, FirebaseItemSubscription>()
  private isDestroyed = false

  /**
   * Base path
   * Example: 'commands', 'orders', etc.
   */
  protected abstract firebaseBasePath: string

  private customFirebasePath: string | null = null

  /**
   * Retorna o path do Firebase
   */
  public getFirebasePath (): string | undefined {
    if (this.customFirebasePath) {
      return this.customFirebasePath
    }

    return undefined
  }

  /**
   * Define um path customizado para o Firebase
   */
  public setCustomFirebasePath (path: string | null): void {
    this.customFirebasePath = path
  }

  /** Initialize firebase */
  private static initializeFirebase (): FirebaseService {
    if (!CPBaseFirebaseGateway.firebaseService) {
      CPBaseFirebaseGateway.firebaseService = useFirebase()
    }
    return CPBaseFirebaseGateway.firebaseService
  }

  /** Get database */
  private getDatabase (): Database | null {
    const service = CPBaseFirebaseGateway.initializeFirebase()
    return service.database
  }

  /** Check if firebase is initialized */
  private isFirebaseInitialized (): boolean {
    const service = CPBaseFirebaseGateway.initializeFirebase()
    return service.isInitialized
  }

  /** Validate firebase */
  private validateFirebase (): BaseError | null {
    if (this.isDestroyed) {
      return {
        code: ErrorCodes.ServerCommunication,
        message: 'Gateway has been destroyed'
      }
    }

    if (!this.isFirebaseInitialized() || !this.getDatabase()) {
      return {
        code: ErrorCodes.ServerCommunication,
        message: 'Firebase is not initialized'
      }
    }

    const { isAuthenticated } = useFirebaseAuth()
    if (!isAuthenticated.value) {
      return {
        code: ErrorCodes.ServerCommunication,
        message: 'Firebase is not authenticated'
      }
    }

    return null
  }

  /**
   * Ensures Firebase Auth is authenticated before connecting to Realtime Database.
   * Call this before any realtime operations.
   * Also revives the gateway if it was previously destroyed (e.g. after logout).
   */
  public async ensureAuthenticated (): Promise<void> {
    const { ensureAuthenticated } = useFirebaseAuth()
    await ensureAuthenticated()
    this.isDestroyed = false
  }

  /**
   * Destroys all active gateway instances, cleaning up all RTDB subscriptions.
   * Called during logout to ensure no stale listeners remain.
   */
  public static destroyAll (): void {
    CPBaseFirebaseGateway.activeInstances.forEach((instance) => {
      instance.destroy()
    })
    CPBaseFirebaseGateway.activeInstances.clear()
    CPBaseFirebaseGateway.firebaseService = null
  }

  /**
   * Destroy gateway and cleanup all active subscriptions
   */
  public destroy (): void {
    if (this.isDestroyed) { return }

    this.activeSubscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })

    this.activeSubscriptions.clear()
    this.activeListSubscriptions.clear()
    this.activeItemSubscriptions.clear()
    this.isDestroyed = true
  }

  /** Process firebase item */
  private processFirebaseItem (itemData: any, itemId?: string): Model | null {
    const validationResult = this.dtoSchema.safeParse(itemData)
    if (!validationResult.success) { return null }

    const modelResult = this.dtoToModelMapper(validationResult.data)
    const model = modelResult instanceof Result
      ? (modelResult.isOk() ? modelResult.getValue() : null)
      : modelResult

    if (model && itemId) {
      model.id = itemId
    }

    return model
  }

  /** Process snapshot to list */
  private processSnapshotToList (snapshot: DataSnapshot): Model[] {
    const data = snapshot.val()
    if (data === null) { return [] }

    const items: Model[] = []
    Object.entries(data).forEach(([itemId, itemData]: [string, any]) => {
      if (!itemData) { return }
      const model = this.processFirebaseItem(itemData, itemId)
      if (model) { items.push(model) }
    })

    return items
  }

  /**
   * Realtime list
   */
  public useRealtimeList (
    path: string
  ): {
    data: Ref<Model[]>
    error: Ref<BaseError | null>
    isConnected: Ref<boolean>
    invalidItemsCount: Ref<number>
    unsubscribe: UnsubscribeFunction
  } {
    const validationError = this.validateFirebase()
    if (validationError) {
      const data = ref<Model[]>([]) as Ref<Model[]>
      const error = ref<BaseError | null>(validationError)
      const isConnected = ref(false)
      const invalidItemsCount = ref(0)
      return {
        data,
        error,
        isConnected,
        invalidItemsCount,
        unsubscribe: (): void => {}
      }
    }

    const existingSubscription = this.activeListSubscriptions.get(path)
    if (existingSubscription) {
      existingSubscription.refCount++

      const unsubscribe = (): void => {
        const subscription = this.activeListSubscriptions.get(path)
        if (!subscription) { return }

        subscription.refCount--

        if (subscription.refCount <= 0) {
          subscription.unsubscribe()
        }
      }

      return {
        data: existingSubscription.data,
        error: existingSubscription.error,
        isConnected: existingSubscription.isConnected,
        invalidItemsCount: existingSubscription.invalidItemsCount,
        unsubscribe
      }
    }

    const data = ref<Model[]>([]) as Ref<Model[]>
    const error = ref<BaseError | null>(null)
    const isConnected = ref(false)
    const invalidItemsCount = ref(0)

    const database = this.getDatabase()!
    const resourceRef = firebaseRef(database, path)

    onValue(
      resourceRef,
      (snapshot) => {
        const snapshotData = snapshot.val()
        if (snapshotData === null) {
          data.value = []
          error.value = null
          isConnected.value = true
          invalidItemsCount.value = 0
          return
        }

        const items: Model[] = []
        let invalidCount = 0

        Object.entries(snapshotData).forEach(([itemId, itemData]: [string, any]) => {
          if (!itemData) { return }
          const model = this.processFirebaseItem(itemData, itemId)
          if (model) {
            items.push(model)
          } else {
            invalidCount++
          }
        })

        data.value = items
        invalidItemsCount.value = invalidCount
        error.value = invalidCount > 0
          ? { code: ErrorCodes.DataValidation, message: `${invalidCount} invalid items ignored` }
          : null
        isConnected.value = true
      },
      (err) => {
        error.value = { code: ErrorCodes.ServerCommunication, message: err.message }
        isConnected.value = false
      }
    )

    const internalUnsubscribe = (): void => {
      off(resourceRef)
      this.activeSubscriptions.delete(path)
      this.activeListSubscriptions.delete(path)
    }

    const unsubscribe = (): void => {
      const subscription = this.activeListSubscriptions.get(path)
      if (!subscription) { return }

      subscription.refCount--

      if (subscription.refCount <= 0) {
        internalUnsubscribe()
      }
    }

    const subscription: FirebaseListSubscription = {
      reference: resourceRef,
      unsubscribe: internalUnsubscribe,
      path,
      data,
      error,
      isConnected,
      invalidItemsCount,
      refCount: 1
    }

    this.activeSubscriptions.set(path, subscription)
    this.activeListSubscriptions.set(path, subscription)
    CPBaseFirebaseGateway.activeInstances.add(this)

    return { data, error, isConnected, invalidItemsCount, unsubscribe }
  }

  /**
   * Realtime item
   */
  public useRealtimeItem (
    path: string
  ): {
    data: Ref<Model | null>
    error: Ref<BaseError | null>
    isConnected: Ref<boolean>
    unsubscribe: UnsubscribeFunction
  } {
    const validationError = this.validateFirebase()
    if (validationError) {
      const data = ref<Model | null>(null) as Ref<Model | null>
      const error = ref<BaseError | null>(validationError)
      const isConnected = ref(false)
      return {
        data,
        error,
        isConnected,
        unsubscribe: (): void => {}
      }
    }

    const existingSubscription = this.activeItemSubscriptions.get(path)
    if (existingSubscription) {
      existingSubscription.refCount++

      const unsubscribe = (): void => {
        const subscription = this.activeItemSubscriptions.get(path)
        if (!subscription) { return }

        subscription.refCount--

        if (subscription.refCount <= 0) {
          subscription.unsubscribe()
        }
      }

      return {
        data: existingSubscription.data,
        error: existingSubscription.error,
        isConnected: existingSubscription.isConnected,
        unsubscribe
      }
    }

    const data = ref<Model | null>(null) as Ref<Model | null>
    const error = ref<BaseError | null>(null)
    const isConnected = ref(false)

    const database = this.getDatabase()!
    const resourceRef = firebaseRef(database, path)

    onValue(
      resourceRef,
      (snapshot) => {
        const snapshotData = snapshot.val()
        if (snapshotData === null) {
          error.value = { code: ErrorCodes.NotFound, message: 'Item not found' }
          isConnected.value = false
          data.value = null
          return
        }

        const model = this.processFirebaseItem(snapshotData)
        if (model) {
          data.value = model
          error.value = null
          isConnected.value = true
        } else {
          error.value = { code: ErrorCodes.DataValidation, message: 'Invalid data received' }
          isConnected.value = false
        }
      },
      (err) => {
        error.value = { code: ErrorCodes.ServerCommunication, message: err.message }
        isConnected.value = false
      }
    )

    const internalUnsubscribe = (): void => {
      off(resourceRef)
      this.activeSubscriptions.delete(path)
      this.activeItemSubscriptions.delete(path)
    }

    const unsubscribe = (): void => {
      const subscription = this.activeItemSubscriptions.get(path)
      if (!subscription) { return }

      subscription.refCount--

      if (subscription.refCount <= 0) {
        internalUnsubscribe()
      }
    }

    const subscription: FirebaseItemSubscription = {
      reference: resourceRef,
      unsubscribe: internalUnsubscribe,
      path,
      data,
      error,
      isConnected,
      refCount: 1
    }

    this.activeSubscriptions.set(path, subscription)
    this.activeItemSubscriptions.set(path, subscription)
    CPBaseFirebaseGateway.activeInstances.add(this)

    return { data, error, isConnected, unsubscribe }
  }

  /**
   * One-time read
   */
  public getSnapshot (
    path: string
  ): Promise<Result<Model[]>> {
    const validationError = this.validateFirebase()
    if (validationError) {
      return Promise.resolve(Result.ofError(validationError))
    }

    const database = this.getDatabase()!
    const resourceRef = firebaseRef(database, path)

    return new Promise((resolve) => {
      onValue(
        resourceRef,
        snapshot => resolve(Result.ofValue(this.processSnapshotToList(snapshot))),
        error => resolve(Result.ofError({ code: ErrorCodes.ServerCommunication, message: error.message })),
        { onlyOnce: true }
      )
    })
  }
}
